import React, { useMemo } from "react";

import { capitalize, groupBy } from "lodash";
import { useSelector } from "react-redux";
import styled from "styled-components/macro";

import Badge from "components/Badge";

import {
  ClearingSaleBuyerStatus,
  ClearingSaleLotStatus,
} from "constants/clearingSaleAttributes";
import { PaymentMethod } from "constants/payments";

import { EMPTY_ARRAY, formatDecimal, formatDollarsFromCents } from "lib";
import { pluralize } from "lib/pluralize";

import {
  getSaleLotIdsByConsignmentId,
  selectConsignmentIdsSaleLotIdsByBuyerIdLookup,
  selectBuyerInvoiceTotalCentsBySaleLotIdLookup,
  getBuyerStatusByBuyerId,
  getPayments,
  getSaleLots,
} from "selectors";

const StatusCountsWrapper = styled.div`
  ${props => props.hasContentAbove && "margin-top: 4px;"}
`;

const formatStatusCount = (status, count, amountCents = null) => {
  const capitalizedStatus = capitalize(status);
  // Vendors see the dollar amount for the status group; buyers (amountCents
  // is null here) see the lot count spelled out instead.
  const suffixDisplay =
    amountCents !== null
      ? ` (${formatDollarsFromCents(amountCents, { format: true })})`
      : ` ${pluralize("lot", count)}`;
  return `${count} ${capitalizedStatus}${suffixDisplay}`;
};

export const ConsignmentSaleLotSummary = React.memo(
  ({ consignmentId, buyerId, expanded = false }) => {
    const saleLotIds =
      useSelector(state =>
        buyerId
          ? selectConsignmentIdsSaleLotIdsByBuyerIdLookup(state)[buyerId][
              consignmentId
            ]
          : getSaleLotIdsByConsignmentId(consignmentId)(state),
      ) || EMPTY_ARRAY;

    return <SaleLotSummary saleLotIds={saleLotIds} expanded={expanded} />;
  },
);

export const SaleLotSummary = React.memo(
  ({ saleLotIds, expanded = false, showPayments = false }) => {
    const saleLotByIdLookup = useSelector(getSaleLots);
    const saleLots = useMemo(
      () => saleLotIds.map(saleLotId => saleLotByIdLookup[saleLotId]),
      [saleLotIds, saleLotByIdLookup],
    );
    const payments = useSelector(getPayments);
    const invoiceTotalsBySaleLotId = useSelector(
      selectBuyerInvoiceTotalCentsBySaleLotIdLookup,
    );

    // Show condensed view (just count) for vendor header (not expanded, not showing payments)
    if (!expanded && !showPayments) {
      return `${saleLotIds.length} Lots`;
    }

    const totalInvoiceCents = showPayments
      ? saleLots.reduce(
          (sum, lot) => sum + (invoiceTotalsBySaleLotId[lot.id] || 0),
          0,
        )
      : 0;

    const buyerId = showPayments
      ? saleLots.find(lot => lot?.buyer_id)?.buyer_id
      : null;

    const buyerPayments =
      showPayments && buyerId
        ? Object.values(payments).filter(
            payment => payment.fromBusinessId === buyerId,
          )
        : EMPTY_ARRAY;

    // An Account payment defers settlement rather than paying it off, so it
    // shouldn't count toward what's been paid (or reduce what's still due).
    const totalPaymentsCents = buyerPayments
      .filter(payment => payment.paymentMethod !== PaymentMethod.ACCOUNT)
      .reduce((sum, payment) => sum + (payment.totalAmountCents || 0), 0);

    // The backend no longer flags a lot Paid from an Account-only payment,
    // but that fix isn't retroactive - lots marked Paid that way before it
    // shipped are still stored as Paid. Reclassify them as Sold here rather
    // than running a one-off data migration to fix the historical rows.
    const isAccountOnly =
      buyerPayments.length > 0 &&
      buyerPayments.every(
        payment => payment.paymentMethod === PaymentMethod.ACCOUNT,
      );

    const totalOutstandingCents = totalInvoiceCents - totalPaymentsCents;

    const saleLotsGroupedByStatus = groupBy(saleLots, saleLot => {
      const status = saleLot?.clearingSaleAttributes?.status;
      return isAccountOnly && status === ClearingSaleLotStatus.PAID
        ? ClearingSaleLotStatus.SOLD
        : status;
    });

    const statusCounts = Object.entries(saleLotsGroupedByStatus).map(
      ([status, statusLots]) => {
        const count = statusLots.length;
        // For vendors (when not showPayments), show total_price_cents per status
        // For buyers (when showPayments), don't show amounts in status counts
        const amountCents = !showPayments
          ? statusLots.reduce(
              (sum, lot) => sum + (lot.total_price_cents || 0),
              0,
            )
          : null;
        return { status, count, amountCents };
      },
    );

    return (
      <div>
        {showPayments && (
          <div>
            <strong>${formatDecimal(totalOutstandingCents / 100)} Due</strong>
          </div>
        )}
        {expanded && showPayments && (
          <>
            <div>${formatDecimal(totalInvoiceCents / 100)} Total</div>
            <div>${formatDecimal(totalPaymentsCents / 100)} Paid</div>
          </>
        )}
        {statusCounts.length > 0 && (
          <StatusCountsWrapper hasContentAbove={showPayments}>
            {showPayments
              ? // Buyers: show on one line
                statusCounts
                  .map(({ status, count, amountCents }) =>
                    formatStatusCount(status, count, amountCents),
                  )
                  .join(", ")
              : // Vendors: show each on separate lines
                statusCounts.map(({ status, count, amountCents }) => (
                  <div key={status}>
                    {formatStatusCount(status, count, amountCents)}
                  </div>
                ))}
          </StatusCountsWrapper>
        )}
      </div>
    );
  },
);

const BuyerStatusColorMap = {
  [ClearingSaleBuyerStatus.PAID]: "paid",
  [ClearingSaleBuyerStatus.PARTIALLY_PAID]: "warning",
  [ClearingSaleBuyerStatus.ON_ACCOUNT]: "warning",
  [ClearingSaleBuyerStatus.SOLD]: "sold",
};

export const BuyerLotStatus = ({ buyerId }) => {
  const status = useSelector(getBuyerStatusByBuyerId(buyerId));

  return <Badge color={BuyerStatusColorMap[status]}>{status}</Badge>;
};
